/**
 * Pipeline unificado de fundo virtual (blur / imagem) — usado em sala, prejoin e modal.
 *
 * Princípio (out/2026 — "plano de fundo deixa a imagem ruim"): **segmentar em baixa
 * resolução, publicar na resolução do perfil.** O MediaPipe já reduz o frame internamente
 * para o modelo (256×256); não é preciso rebaixar a captura da câmera inteira para 270p.
 * Em desktop normal a câmera continua no perfil do usuário (540p/720p @ 24 fps) e só um
 * cap moderado de bitrate é aplicado. A captura só cai por **medição** (watchdog de
 * tempo por frame) e volta a subir quando o processamento fica folgado.
 *
 * Tiers de captura (do melhor ao mais leve): full → light → weak → minimal.
 * - Dispositivo normal começa em `full`; dispositivo fraco (≤4 núcleos / <4 GB) em `weak`.
 * - Após um failsafe (processor pausado por lentidão), a reaplicação começa um tier abaixo
 *   do inicial e o watchdog pode recuperar gradualmente.
 */

import toast from 'react-hot-toast';
import type { LocalTrackPublication, LocalVideoTrack } from 'livekit-client';
import { Track } from 'livekit-client';
import { isWeakClientDevice } from '@/lib/client-utils';
import {
  capLocalVideoTrackPublishBitrate,
  restoreLocalVideoTrackPublishBitrate,
  type CameraPublishBitrateCap,
} from '@/lib/camera-publish-bitrate';
import {
  getVideoCaptureMediaConstraints,
  getVideoQualityRoomCapture,
  readVideoQualityProfile,
  type VideoQualityProfile,
} from '@/lib/video-quality-profile';
import { optimizeImageForBackground, shouldOptimizeImage } from '@/lib/imageOptimization';

export type BackgroundType = 'none' | 'blur' | 'image';

export const BACKGROUND_DEBOUNCE_MS = 600;

/** Tiers de captura com processor ativo, do mais nítido ao mais leve. */
export type ProcessorCaptureTier = 'full' | 'light' | 'weak' | 'minimal';
export const PROCESSOR_CAPTURE_TIERS: readonly ProcessorCaptureTier[] = [
  'full',
  'light',
  'weak',
  'minimal',
];

/**
 * `full`: captura do perfil do usuário, limitada a 720p @ 24 fps — o composite WebGL em
 * 1080p não compensa o custo e a câmera com fundo não precisa passar de 720p.
 */
export function getFullTierCaptureConstraints(profile: VideoQualityProfile): MediaTrackConstraints {
  if (profile === 'high' || profile === 'max') {
    return {
      width: { ideal: 1280, max: 1280 },
      height: { ideal: 720, max: 720 },
      frameRate: { ideal: 24, max: 24 },
    };
  }
  return {
    width: { ideal: 960, max: 1280 },
    height: { ideal: 540, max: 720 },
    frameRate: { ideal: 24, max: 24 },
  };
}

/** `light`: 1º degrau de degradação em desktop — ainda nítido em grade e gravação. */
export const PROCESSOR_CAPTURE_CONSTRAINTS_LIGHT: MediaTrackConstraints = {
  width: { ideal: 640, max: 960 },
  height: { ideal: 360, max: 540 },
  frameRate: { ideal: 20, max: 24 },
};

/** `weak`: ponto de partida em dispositivo fraco (perfil "leve" de 07/2026). */
export const PROCESSOR_CAPTURE_CONSTRAINTS: MediaTrackConstraints = {
  width: { ideal: 480, max: 640 },
  height: { ideal: 270, max: 360 },
  frameRate: { ideal: 12, max: 15 },
};

/** `minimal`: último degrau antes de pausar o processor. */
export const PROCESSOR_CAPTURE_CONSTRAINTS_DEGRADED: MediaTrackConstraints = {
  width: { ideal: 320, max: 480 },
  height: { ideal: 180, max: 270 },
  frameRate: { ideal: 8, max: 10 },
};

/**
 * Teto de publish por tier (mantém TODAS as camadas de simulcast — ver regra crítica em
 * camera-publish-bitrate.ts). `full` usa ~75 % do perfil: 540p a 650 kbps equivale ao
 * preset h540 do LiveKit; 720p a 1 Mbps. Os tiers leves mantêm o cap de 400 kbps / 15 fps.
 */
export function getProcessorTierBitrateCap(
  tier: ProcessorCaptureTier,
  profile: VideoQualityProfile,
): CameraPublishBitrateCap {
  switch (tier) {
    case 'full': {
      const profileBitrate = getVideoQualityRoomCapture(profile).publishDefaults.videoEncoding
        ?.maxBitrate;
      const base = typeof profileBitrate === 'number' ? Math.round(profileBitrate * 0.75) : 650_000;
      const maxBitrate = Math.min(Math.max(base, 650_000), 1_000_000);
      return { maxBitrate, maxFramerate: 24, proportional: true };
    }
    case 'light':
      return { maxBitrate: 500_000, maxFramerate: 20, proportional: true };
    case 'weak':
    case 'minimal':
    default:
      return { maxBitrate: 400_000, maxFramerate: 15, proportional: true };
  }
}

export function getProcessorTierCaptureConstraints(
  tier: ProcessorCaptureTier,
  profile: VideoQualityProfile,
): MediaTrackConstraints {
  switch (tier) {
    case 'full':
      return getFullTierCaptureConstraints(profile);
    case 'light':
      return PROCESSOR_CAPTURE_CONSTRAINTS_LIGHT;
    case 'weak':
      return PROCESSOR_CAPTURE_CONSTRAINTS;
    case 'minimal':
    default:
      return PROCESSOR_CAPTURE_CONSTRAINTS_DEGRADED;
  }
}

/**
 * Raio do blur em texels do frame de saída — escala com a altura capturada para o
 * desfoque parecer o mesmo em 270p e 540p (6 px em 270p ≈ 12 px em 540p).
 */
export function getBlurRadiusForTier(tier: ProcessorCaptureTier, profile: VideoQualityProfile): number {
  switch (tier) {
    case 'full':
      return profile === 'high' || profile === 'max' ? 15 : 12;
    case 'light':
      return 9;
    case 'weak':
      return 6;
    case 'minimal':
    default:
      return 4;
  }
}

/** FPS máximo do processor (frames acima disso são descartados antes do MediaPipe). */
export function getProcessorMaxFpsForTier(tier: ProcessorCaptureTier): number {
  return tier === 'full' || tier === 'light' ? 24 : 15;
}

/** Tier inicial por dispositivo; após failsafe recente começa um degrau abaixo. */
export function initialProcessorTier(weakDevice: boolean, afterFailsafe = false): ProcessorCaptureTier {
  if (weakDevice) return afterFailsafe ? 'minimal' : 'weak';
  return afterFailsafe ? 'light' : 'full';
}

export function nextLowerTier(tier: ProcessorCaptureTier): ProcessorCaptureTier {
  const idx = PROCESSOR_CAPTURE_TIERS.indexOf(tier);
  return PROCESSOR_CAPTURE_TIERS[Math.min(idx + 1, PROCESSOR_CAPTURE_TIERS.length - 1)]!;
}

export function nextHigherTier(tier: ProcessorCaptureTier): ProcessorCaptureTier {
  const idx = PROCESSOR_CAPTURE_TIERS.indexOf(tier);
  return PROCESSOR_CAPTURE_TIERS[Math.max(idx - 1, 0)]!;
}

function tierIsBelow(a: ProcessorCaptureTier, b: ProcessorCaptureTier): boolean {
  return PROCESSOR_CAPTURE_TIERS.indexOf(a) > PROCESSOR_CAPTURE_TIERS.indexOf(b);
}

/** Se o MediaPipe passar disso em frames consecutivos, desce um tier. */
export const SLOW_FRAME_MS = 40;
export const SLOW_FRAME_SAMPLES = 12;
/** Já em `minimal` e ainda lento: pausa o processor (evita “só fundo” travado). */
export const FAILSAFE_SLOW_SAMPLES_AFTER_DEGRADE = 12;
/** Frame "folgado": abaixo disso conta para recuperar um tier. */
export const FAST_FRAME_MS = 20;
/** Frames folgados consecutivos para subir um tier (dobra a cada recuperação). */
export const RECOVER_FAST_SAMPLES = 90;
export const MAX_TIER_RECOVERIES = 3;
export const BACKGROUND_FAILSAFE_EVENT = 'evolumeet:background-failsafe';
/** Disparado quando o tier de captura muda (diagnóstico / UI). detail: { tier }. */
export const BACKGROUND_TIER_EVENT = 'evolumeet:background-tier-changed';
/** Reaplica o fundo após pausa (MediaPipe lento / CPU constrained). */
export const BACKGROUND_FAILSAFE_RETRY_MS = 15_000;
/** Janela em que um failsafe anterior faz a reaplicação começar um tier abaixo. */
export const FAILSAFE_MEMORY_MS = 10 * 60_000;

export type BackgroundPreference = { type: BackgroundType; imagePath?: string };

/** Failsafe só pausa o processor — não grava "none" no localStorage. */
export function shouldPersistBackgroundNoneOnFailsafe(): boolean {
  return false;
}

export function planBackgroundFailsafeRetry(
  current: BackgroundPreference | null,
): { persistNone: boolean; retry: BackgroundPreference | null; retryMs: number } {
  if (!current || current.type === 'none') {
    return { persistNone: false, retry: null, retryMs: 0 };
  }
  return {
    persistNone: shouldPersistBackgroundNoneOnFailsafe(),
    retry: current,
    retryMs: BACKGROUND_FAILSAFE_RETRY_MS,
  };
}

export function notifyBackgroundPaused(): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(new CustomEvent(BACKGROUND_FAILSAFE_EVENT));
}

export type ProcessorHealthAction = 'none' | 'degrade' | 'recover' | 'disable';

export type ProcessorHealthState = {
  tier: ProcessorCaptureTier;
  /** Melhor tier permitido para este track (tier inicial do dispositivo). */
  ceilingTier: ProcessorCaptureTier;
  slowCount: number;
  fastCount: number;
  failsafeCount: number;
  recoveryCount: number;
};

export type ProcessorHealthDecision = {
  action: ProcessorHealthAction;
  next: ProcessorHealthState;
};

/**
 * Decisão pura do watchdog de frame do MediaPipe — bidirecional:
 * - lento (> SLOW_FRAME_MS) por SLOW_FRAME_SAMPLES frames seguidos → desce um tier;
 *   já em `minimal` → após FAILSAFE_SLOW_SAMPLES_AFTER_DEGRADE pausa o processor;
 * - folgado (< FAST_FRAME_MS) por RECOVER_FAST_SAMPLES × 2^recuperações → sobe um tier
 *   (nunca acima de `ceilingTier`, no máximo MAX_TIER_RECOVERIES vezes).
 */
export function nextProcessorHealthAction(input: {
  processingTimeMs: number;
  state: ProcessorHealthState;
}): ProcessorHealthDecision {
  const s = input.state;
  const slow = input.processingTimeMs > SLOW_FRAME_MS;
  const fast = input.processingTimeMs < FAST_FRAME_MS;

  if (slow) {
    if (s.tier !== 'minimal') {
      const slowCount = s.slowCount + 1;
      if (slowCount >= SLOW_FRAME_SAMPLES) {
        return {
          action: 'degrade',
          next: { ...s, tier: nextLowerTier(s.tier), slowCount: 0, fastCount: 0, failsafeCount: 0 },
        };
      }
      return { action: 'none', next: { ...s, slowCount, fastCount: 0 } };
    }
    const failsafeCount = s.failsafeCount + 1;
    if (failsafeCount >= FAILSAFE_SLOW_SAMPLES_AFTER_DEGRADE) {
      return { action: 'disable', next: { ...s, failsafeCount, fastCount: 0 } };
    }
    return { action: 'none', next: { ...s, failsafeCount, fastCount: 0 } };
  }

  // Frame dentro do orçamento: zera contadores de lentidão.
  const base: ProcessorHealthState = { ...s, slowCount: 0, failsafeCount: 0 };
  if (!fast || !tierIsBelow(s.tier, s.ceilingTier) || s.recoveryCount >= MAX_TIER_RECOVERIES) {
    return { action: 'none', next: base };
  }
  const fastCount = s.fastCount + 1;
  const needed = RECOVER_FAST_SAMPLES * 2 ** s.recoveryCount;
  if (fastCount >= needed) {
    return {
      action: 'recover',
      next: {
        ...base,
        tier: nextHigherTier(s.tier),
        fastCount: 0,
        recoveryCount: s.recoveryCount + 1,
      },
    };
  }
  return { action: 'none', next: { ...base, fastCount } };
}

type VideoProcessor = Parameters<LocalVideoTrack['setProcessor']>[0] & { name?: string };

type TrackProcessorImport = () => Promise<{
  BackgroundBlur: (
    blurRadius?: number,
    segmenterOptions?: Record<string, unknown>,
    onFrameProcessed?: (stats: { processingTimeMs: number }) => void,
    processorOptions?: { maxFps?: number },
  ) => unknown;
  VirtualBackground: (
    imagePath: string,
    segmenterOptions?: Record<string, unknown>,
    onFrameProcessed?: (stats: { processingTimeMs: number }) => void,
    processorOptions?: { maxFps?: number },
  ) => unknown;
}>;

let processorsPromise: TrackProcessorImport | null = null;

const appliedSignatureByTrack = new WeakMap<LocalVideoTrack, string>();
/** Estado do watchdog por track (tier atual, contadores). Ausente = processor inativo. */
const healthStateByTrack = new WeakMap<LocalVideoTrack, ProcessorHealthState>();
/** Geração do processor: handlers de um processor antigo (já parado) são ignorados. */
const processorGenerationByTrack = new WeakMap<LocalVideoTrack, number>();
/** Último failsafe (ms epoch) — reaplicação recente começa um tier abaixo. */
const lastFailsafeAtByTrack = new WeakMap<LocalVideoTrack, number>();
/** Tracks cujo processor foi pausado pelo failsafe e ainda não reaplicado. */
const pausedByFailsafe = new WeakSet<LocalVideoTrack>();
/** Último tier cujo teto de bitrate foi aplicado ao sender (para saber se está subindo). */
const appliedCapTierByTrack = new WeakMap<LocalVideoTrack, ProcessorCaptureTier>();

/** Tier de captura ativo para o track (undefined = sem processor). Diagnóstico/UI. */
export function getProcessorCaptureTier(track: LocalVideoTrack): ProcessorCaptureTier | undefined {
  return healthStateByTrack.get(track)?.tier;
}

function emitTierChanged(tier: ProcessorCaptureTier | null): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(BACKGROUND_TIER_EVENT, { detail: { tier } }));
}

function hadRecentFailsafe(track: LocalVideoTrack, now = Date.now()): boolean {
  const at = lastFailsafeAtByTrack.get(track);
  return typeof at === 'number' && now - at < FAILSAFE_MEMORY_MS;
}

/** Estado inicial do watchdog para uma nova aplicação de processor. */
export function createInitialHealthState(
  weakDevice: boolean,
  afterFailsafe: boolean,
): ProcessorHealthState {
  return {
    tier: initialProcessorTier(weakDevice, afterFailsafe),
    ceilingTier: initialProcessorTier(weakDevice, false),
    slowCount: 0,
    fastCount: 0,
    failsafeCount: 0,
    recoveryCount: 0,
  };
}

async function loadProcessors(
  importTrackProcessors?: TrackProcessorImport,
): Promise<Awaited<ReturnType<TrackProcessorImport>>> {
  const loader = importTrackProcessors ?? (async () => import('@livekit/track-processors'));
  if (!processorsPromise) {
    processorsPromise = loader;
  }
  return processorsPromise();
}

function backgroundSignature(
  type: BackgroundType,
  imagePath: string | undefined,
  shouldApply: boolean,
): string {
  if (!shouldApply || type === 'none') return 'none';
  if (type === 'blur') return 'blur';
  return `image:${imagePath ?? ''}`;
}

function processorNameForType(type: BackgroundType): string | null {
  if (type === 'blur') return 'background-blur';
  if (type === 'image') return 'virtual-background';
  return null;
}

function getExistingProcessorName(track: LocalVideoTrack): string | undefined {
  const proc = (
    track as LocalVideoTrack & { getProcessor?: () => { name?: string } | undefined }
  ).getProcessor?.();
  return proc?.name;
}

/**
 * Captura + teto de publish conforme o tier do processor (API livekit-client).
 *
 * `processorActive=true`: aplica o tier atual do track (ou `opts.tier`); se o track ainda
 * não tem estado (1ª aplicação), cria o estado inicial para o dispositivo.
 * `processorActive=false`: volta captura e bitrate ao perfil do usuário e limpa o estado.
 */
export async function applyProcessorCaptureProfile(
  track: LocalVideoTrack,
  processorActive: boolean,
  opts?: { tier?: ProcessorCaptureTier; afterFailsafe?: boolean },
): Promise<void> {
  const mediaTrack = track.mediaStreamTrack;
  if (!mediaTrack) {
    return;
  }

  try {
    if (processorActive) {
      let state = healthStateByTrack.get(track);
      if (!state) {
        state = createInitialHealthState(
          isWeakClientDevice(),
          opts?.afterFailsafe ?? hadRecentFailsafe(track),
        );
        healthStateByTrack.set(track, state);
      }
      if (opts?.tier && opts.tier !== state.tier) {
        state = { ...state, tier: opts.tier };
        healthStateByTrack.set(track, state);
      }
      const profile = readVideoQualityProfile();
      // Subindo de tier (recover): o cap só reduz encodings, então primeiro volta ao
      // perfil e depois aplica o teto do novo tier. Descendo/1ª vez: cap direto, assim
      // um teto externo mais baixo (ex.: câmera-thumbnail no screen share) é preservado.
      const previousTier = appliedCapTierByTrack.get(track);
      if (previousTier && tierIsBelow(previousTier, state.tier)) {
        await restoreLocalVideoTrackPublishBitrate(track);
      }
      // NÃO usar track.prioritizePerformance(): ele desativa 2 das 3 camadas de
      // simulcast e o SFU marca o publisher como Poor (indicador vermelho) assim
      // que a câmera liga com fundo virtual. Só capamos bitrate/fps, proporcional.
      await capLocalVideoTrackPublishBitrate(track, getProcessorTierBitrateCap(state.tier, profile));
      appliedCapTierByTrack.set(track, state.tier);
      await mediaTrack.applyConstraints(getProcessorTierCaptureConstraints(state.tier, profile));
      return;
    }

    healthStateByTrack.delete(track);
    appliedCapTierByTrack.delete(track);
    await mediaTrack.applyConstraints(getVideoCaptureMediaConstraints(readVideoQualityProfile()));
    await restoreLocalVideoTrackPublishBitrate(track);
  } catch (e) {
    console.warn('[background-processor] capture profile:', e);
  }
}

async function disableProcessorAfterFailsafe(track: LocalVideoTrack): Promise<void> {
  try {
    lastFailsafeAtByTrack.set(track, Date.now());
    pausedByFailsafe.add(track);
    await track.stopProcessor().catch(() => {});
    await applyProcessorCaptureProfile(track, false);
    appliedSignatureByTrack.set(track, 'none');
    emitTierChanged(null);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(BACKGROUND_FAILSAFE_EVENT));
    }
    toast('Fundo virtual pausado temporariamente — tentaremos reativar.', {
      icon: 'ℹ️',
      duration: 4500,
    });
    console.warn('[background-processor] failsafe: processor pausado (MediaPipe lento demais)');
  } catch (error) {
    console.warn('[background-processor] failsafe:', error);
  }
}

/**
 * Sinal externo de CPU saturada (`LocalTrackCpuConstrained`): desce um tier imediatamente
 * e desliga a recuperação automática nesta sessão do processor — o encoder já avisou que
 * não há folga, não faz sentido o watchdog tentar subir de novo. Sem processor ativo
 * (ou já em `minimal`) é no-op e devolve false.
 */
export async function notifyProcessorCpuConstrained(track: LocalVideoTrack): Promise<boolean> {
  const state = healthStateByTrack.get(track);
  if (!state || pausedByFailsafe.has(track)) {
    return false;
  }
  const next: ProcessorHealthState = {
    ...state,
    tier: nextLowerTier(state.tier),
    slowCount: 0,
    fastCount: 0,
    failsafeCount: 0,
    recoveryCount: MAX_TIER_RECOVERIES,
  };
  healthStateByTrack.set(track, next);
  if (next.tier === state.tier) {
    return false;
  }
  await applyProcessorCaptureProfile(track, true, { tier: next.tier });
  emitTierChanged(next.tier);
  console.warn(`[background-processor] tier ${state.tier} → ${next.tier} (cpu constrained)`);
  return true;
}

function createFrameHealthHandler(track: LocalVideoTrack, generation: number) {
  return (stats: { processingTimeMs: number }) => {
    if (processorGenerationByTrack.get(track) !== generation || pausedByFailsafe.has(track)) {
      return;
    }
    const state = healthStateByTrack.get(track);
    if (!state) {
      return;
    }
    const decision = nextProcessorHealthAction({
      processingTimeMs: stats.processingTimeMs,
      state,
    });
    healthStateByTrack.set(track, decision.next);

    if (decision.action === 'degrade' || decision.action === 'recover') {
      void applyProcessorCaptureProfile(track, true, { tier: decision.next.tier });
      emitTierChanged(decision.next.tier);
      console.warn(
        `[background-processor] tier ${state.tier} → ${decision.next.tier} (${decision.action}):`,
        Math.round(stats.processingTimeMs),
        'ms/frame',
      );
      return;
    }

    if (decision.action === 'disable') {
      void disableProcessorAfterFailsafe(track);
    }
  };
}

function buildSegmenterOptions(): Record<string, unknown> {
  const weak = isWeakClientDevice();
  const opts: Record<string, unknown> = { delegate: weak ? 'CPU' : 'GPU' };
  if (weak) {
    opts.modelAssetPath =
      'https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter/float16/latest/selfie_segmenter.tflite';
  }
  return opts;
}

async function resolveOptimizedImage(imagePath: string): Promise<string> {
  if (!imagePath.startsWith('data:') || !shouldOptimizeImage(imagePath)) {
    return imagePath;
  }
  const result = await optimizeImageForBackground(imagePath);
  if (result.success && result.dataUrl) {
    return result.dataUrl;
  }
  return imagePath;
}

async function waitForLiveTrack(
  track: LocalVideoTrack,
  publication: LocalTrackPublication | undefined,
  delay: (ms: number) => Promise<void>,
  maxAttempts = 8,
): Promise<'ready' | 'muted' | 'timeout'> {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (!track.mediaStreamTrack) {
      await delay(300);
      continue;
    }
    if (publication?.isMuted) {
      // Câmera desligada — não é erro; reaplicamos no LocalTrackPublished.
      return 'muted';
    }
    if (track.mediaStreamTrack.readyState === 'ended') {
      try {
        await track.restartTrack();
        await delay(400);
      } catch {
        await delay(300);
      }
      continue;
    }
    if (track.mediaStreamTrack.readyState === 'live' && !publication?.isMuted) {
      return 'ready';
    }
    await delay(300);
  }
  if (
    track.mediaStreamTrack &&
    track.mediaStreamTrack.readyState === 'live' &&
    !publication?.isMuted
  ) {
    return 'ready';
  }
  if (publication?.isMuted) {
    return 'muted';
  }
  return 'timeout';
}

function isGpuProcessorError(error: unknown): boolean {
  const msg = (error instanceof Error ? error.message : String(error)).toLowerCase();
  return /gpu|webgl|gl_|delegate|mediapipe|wasm|out of memory/i.test(msg);
}

async function applyVideoProcessor(
  track: LocalVideoTrack,
  build: (segmenterOptions: Record<string, unknown>) => VideoProcessor,
): Promise<void> {
  const gpuOpts = buildSegmenterOptions();
  try {
    await track.setProcessor(build(gpuOpts) as VideoProcessor);
    return;
  } catch (gpuError) {
    if (!isGpuProcessorError(gpuError)) {
      throw gpuError;
    }
    console.warn('[background-processor] GPU falhou — tentando CPU:', gpuError);
    await track.setProcessor(
      build({ ...gpuOpts, delegate: 'CPU' }) as VideoProcessor,
    );
  }
}

export type ApplyBackgroundOptions = {
  targetType: BackgroundType;
  targetImage?: string;
  shouldApplyProcessor: boolean;
  delay: (ms: number) => Promise<void>;
  importTrackProcessors?: TrackProcessorImport;
  /** false = preview modal (sem toast) */
  notifyOnError?: boolean;
  /** Força reaplicar mesmo com a mesma assinatura (troca de device). */
  force?: boolean;
};

export type ApplyBackgroundResult = {
  ok: boolean;
  appliedType: BackgroundType;
  error?: string;
  skipped?: boolean;
  /** Câmera desligada — sem toast; reaplicar quando publicar. */
  deferred?: boolean;
};

export async function applyBackgroundToVideoTrack(
  track: LocalVideoTrack,
  options: ApplyBackgroundOptions,
  publication?: LocalTrackPublication,
): Promise<ApplyBackgroundResult> {
  const notify = options.notifyOnError !== false;
  const ready = await waitForLiveTrack(track, publication, options.delay);
  if (ready === 'muted') {
    return { ok: true, appliedType: 'none', skipped: true, deferred: true };
  }
  if (ready === 'timeout') {
    const msg = 'Câmera indisponível para aplicar fundo virtual';
    if (notify) toast.error(msg);
    return { ok: false, appliedType: 'none', error: msg };
  }

  const sig = backgroundSignature(
    options.targetType,
    options.targetImage,
    options.shouldApplyProcessor,
  );
  const existingName = getExistingProcessorName(track);
  const expectedName = options.shouldApplyProcessor
    ? processorNameForType(options.targetType)
    : null;

  // Evita stop/setProcessor em loop (ex.: LocalTrackPublished após applyConstraints).
  if (
    !options.force &&
    appliedSignatureByTrack.get(track) === sig &&
    ((sig === 'none' && !existingName) || (expectedName && existingName === expectedName))
  ) {
    if (sig !== 'none') {
      await applyProcessorCaptureProfile(track, true);
    }
    return { ok: true, appliedType: sig === 'none' ? 'none' : options.targetType, skipped: true };
  }

  const MAX_ATTEMPTS = 3;
  let optimizedImage = options.targetImage;

  if (
    options.targetType === 'image' &&
    options.targetImage &&
    options.targetImage.startsWith('data:')
  ) {
    optimizedImage = await resolveOptimizedImage(options.targetImage);
  }

  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    try {
      if (!options.shouldApplyProcessor) {
        await track.stopProcessor().catch(() => {});
        await applyProcessorCaptureProfile(track, false);
        appliedSignatureByTrack.set(track, 'none');
        return { ok: true, appliedType: 'none' };
      }

      // 1) Nova aplicação: zera o watchdog (tier inicial do dispositivo; um degrau
      //    abaixo se houve failsafe recente) e ajusta captura/bitrate ANTES do MediaPipe.
      const afterFailsafe = pausedByFailsafe.has(track) || hadRecentFailsafe(track);
      pausedByFailsafe.delete(track);
      healthStateByTrack.set(track, createInitialHealthState(isWeakClientDevice(), afterFailsafe));
      const generation = (processorGenerationByTrack.get(track) ?? 0) + 1;
      processorGenerationByTrack.set(track, generation);
      await applyProcessorCaptureProfile(track, true);
      await options.delay(80);

      await track.stopProcessor().catch(() => {});
      await options.delay(60);

      const { BackgroundBlur, VirtualBackground } = await loadProcessors(
        options.importTrackProcessors,
      );

      const segmenterOptions = buildSegmenterOptions();
      const onFrameProcessed = createFrameHealthHandler(track, generation);
      const profile = readVideoQualityProfile();
      const startTier = healthStateByTrack.get(track)?.tier ?? 'weak';
      const processorOptions = { maxFps: getProcessorMaxFpsForTier(startTier) };
      const blurRadius = getBlurRadiusForTier(startTier, profile);
      emitTierChanged(startTier);

      if (options.targetType === 'blur') {
        await applyVideoProcessor(track, (segmenterOptions) =>
          BackgroundBlur(
            blurRadius,
            segmenterOptions,
            onFrameProcessed,
            processorOptions,
          ) as VideoProcessor,
        );
        appliedSignatureByTrack.set(track, 'blur');
        return { ok: true, appliedType: 'blur' };
      }

      if (options.targetType === 'image' && optimizedImage) {
        await applyVideoProcessor(track, (segmenterOptions) =>
          VirtualBackground(
            optimizedImage,
            segmenterOptions,
            onFrameProcessed,
            processorOptions,
          ) as VideoProcessor,
        );
        appliedSignatureByTrack.set(track, `image:${options.targetImage ?? ''}`);
        return { ok: true, appliedType: 'image' };
      }

      appliedSignatureByTrack.set(track, 'none');
      return { ok: true, appliedType: 'none' };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      const isRecoverable =
        /stream closed|input track cannot be ended|Empty video frame/i.test(errorMessage);

      if (isRecoverable && attempt < MAX_ATTEMPTS - 1) {
        await options.delay(350);
        continue;
      }

      if (options.targetType === 'image' && attempt === MAX_ATTEMPTS - 1) {
        try {
          await track.stopProcessor().catch(() => {});
          await applyProcessorCaptureProfile(track, true);
          const { BackgroundBlur } = await loadProcessors(options.importTrackProcessors);
          const fallbackGeneration = (processorGenerationByTrack.get(track) ?? 0) + 1;
          processorGenerationByTrack.set(track, fallbackGeneration);
          const fallbackTier = healthStateByTrack.get(track)?.tier ?? 'weak';
          await applyVideoProcessor(track, (segmenterOptions) =>
            BackgroundBlur(
              getBlurRadiusForTier(fallbackTier, readVideoQualityProfile()),
              segmenterOptions,
              createFrameHealthHandler(track, fallbackGeneration),
              { maxFps: getProcessorMaxFpsForTier(fallbackTier) },
            ) as VideoProcessor,
          );
          appliedSignatureByTrack.set(track, 'blur');
          if (notify) toast('Imagem indisponível — fundo desfocado aplicado.', { icon: 'ℹ️' });
          return { ok: true, appliedType: 'blur' };
        } catch {
          /* fall through */
        }
      }

      await track.stopProcessor().catch(() => {});
      await applyProcessorCaptureProfile(track, false);
      appliedSignatureByTrack.set(track, 'none');
      if (notify) toast.error('Não foi possível aplicar o fundo virtual.');
      return { ok: false, appliedType: 'none', error: errorMessage };
    }
  }

  return { ok: false, appliedType: 'none', error: 'max attempts' };
}

export async function applyBackgroundProcessorToPublication(
  publication: LocalTrackPublication,
  options: ApplyBackgroundOptions,
): Promise<ApplyBackgroundResult> {
  if (publication.kind !== Track.Kind.Video || publication.source !== Track.Source.Camera) {
    return { ok: true, appliedType: 'none' };
  }

  const track = publication.track as LocalVideoTrack | undefined;
  if (!track) {
    return { ok: false, appliedType: 'none', error: 'no track' };
  }

  return applyBackgroundToVideoTrack(track, options, publication);
}
